import { API_URL } from './config';

// ─── Helpers ──────────────────────────────────────────────────────

function getToken() {
  try { return localStorage.getItem('soul_session_token'); } catch { return null; }
}

function apiHeaders() {
  const t = getToken();
  const h = { 'Content-Type': 'application/json' };
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

const API = API_URL || '';

// Fire-and-forget POST to backend (never blocks UI)
function syncToServer(path, body) {
  const t = getToken();
  if (!t) return; // not logged in — skip server sync
  fetch(`${API}/api${path}`, {
    method: 'POST',
    headers: apiHeaders(),
    body: JSON.stringify(body),
  }).catch(err => console.warn('⚠️ Server sync failed:', path, err.message));
}

// Initialize auto-sync (no auth required)
export async function initAutoSync() {
  console.log('✅ Auto-sync initialized');
}

// ─── Full Sync: Pull everything from server ──────────────────────

export async function pullFromServer() {
  const t = getToken();
  if (!t) return null;
  try {
    const r = await fetch(`${API}/api/user/sync?_t=${Date.now()}`, {
      headers: { ...apiHeaders(), 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    });
    console.log('🔄 pullFromServer response:', r.status, r.statusText);
    if (!r.ok) { console.warn('🔄 pullFromServer failed:', r.status); return null; }
    const data = await r.json();
    console.log('🔄 Pulled sync data:', JSON.stringify(data.user), 'prefs:', !!data.preferences);

    // Apply server data to localStorage
    if (data.voicemail_reactions && Object.keys(data.voicemail_reactions).length > 0) {
      localStorage.setItem('soul_voicemail_reactions', JSON.stringify(data.voicemail_reactions));
    }
    if (data.preferences) {
      if (data.preferences.theme) localStorage.setItem('soul_theme', data.preferences.theme);
      if (data.preferences.color_scheme) localStorage.setItem('soul_color_scheme', data.preferences.color_scheme);
      if (data.preferences.feature_flags) localStorage.setItem('soul_feature_flags', JSON.stringify(data.preferences.feature_flags));
      // Dating preferences
      if (data.preferences.age_range_min != null && data.preferences.age_range_max != null) {
        localStorage.setItem('soul_age_range', JSON.stringify({ min: data.preferences.age_range_min, max: data.preferences.age_range_max }));
        console.log('🔄 Synced age_range:', data.preferences.age_range_min, '-', data.preferences.age_range_max);
      }
      if (data.preferences.max_distance != null) {
        localStorage.setItem('soul_max_distance', String(data.preferences.max_distance));
        console.log('🔄 Synced max_distance:', data.preferences.max_distance);
      }
      if (data.preferences.user_location) {
        localStorage.setItem('soul_user_location', data.preferences.user_location);
      }
      if (data.preferences.app_connections) {
        const conns = typeof data.preferences.app_connections === 'string'
          ? JSON.parse(data.preferences.app_connections)
          : data.preferences.app_connections;
        localStorage.setItem('app_connections', JSON.stringify(conns));
        window.dispatchEvent(new Event('app_connections_changed'));
        console.log('🔄 Synced app_connections');
      }
    }
    if (data.interview_progress && data.interview_progress.length > 0) {
      // Find the most recent incomplete stage for resume
      const incomplete = data.interview_progress.find(p => !p.completed);
      if (incomplete) {
        localStorage.setItem('soul_interview_progress', JSON.stringify({
          stage: incomplete.stage,
          step: incomplete.step,
          answers: incomplete.answers || {},
        }));
      }
    }
    if (data.user) {
      const serverStage = Number(data.user.profile_stage) || 0;
      if (serverStage > 0) {
        // Update completedStages in localStorage
        const s = [];
        for (let i = 1; i <= serverStage; i++) s.push(i);
        localStorage.setItem('soul_completed_stages', JSON.stringify(s));
      }
      // Update soul_user with server data (phone, profile_stage)
      try {
        const u = JSON.parse(localStorage.getItem('soul_user') || '{}');
        if (serverStage > 0) u.profile_stage = Math.max(u.profile_stage || 0, serverStage);
        if (data.user.phone) u.phone = data.user.phone;
        localStorage.setItem('soul_user', JSON.stringify(u));
      } catch {}
    }
    // User answers (interview/dating preferences) → localStorage
    if (data.user_answers && Object.keys(data.user_answers).length > 0) {
      localStorage.setItem('soul_user_answers', JSON.stringify(data.user_answers));
      console.log('🔄 Synced user_answers:', Object.keys(data.user_answers).length, 'keys');
    }
    // Match interactions → localStorage
    if (data.match_interactions && data.match_interactions.length > 0) {
      const givenReactions = {};
      data.match_interactions.forEach(mi => {
        const d = typeof mi.data === 'string' ? JSON.parse(mi.data) : mi.data;
        if (mi.interaction_type === 'given_reaction') {
          givenReactions[mi.match_id] = d.emoji;
        } else if (mi.interaction_type === 'received_gift') {
          localStorage.setItem(`received_gift_${mi.match_id}`, d.gift);
        } else if (mi.interaction_type === 'received_poll') {
          localStorage.setItem(`received_poll_${mi.match_id}`, JSON.stringify(d));
        } else if (mi.interaction_type === 'received_phone') {
          localStorage.setItem(`received_phone_${mi.match_id}`, d.phone);
        } else if (mi.interaction_type === 'received_message') {
          localStorage.setItem(`received_message_${mi.match_id}`, JSON.stringify(d));
        } else if (mi.interaction_type === 'poll_response') {
          localStorage.setItem(`poll_response_${mi.match_id}`, d.option);
        }
      });
      if (Object.keys(givenReactions).length > 0) {
        localStorage.setItem('soul_given_reactions', JSON.stringify(givenReactions));
        window.dispatchEvent(new Event('givenReactionsChanged'));
      }
    }

    // Secret context → localStorage
    if (data.secret_context && data.secret_context.length > 0) {
      const items = data.secret_context.map(r => ({ text: r.text, category: r.category, createdAt: new Date(r.created_at).getTime(), exact: r.exact || false }));
      localStorage.setItem('soul_secret_context', JSON.stringify(items));
      console.log('🔄 Synced secret_context:', items.length, 'items');
    }
    if (data.exact_context && data.exact_context.length > 0) {
      localStorage.setItem('soul_exact_context', JSON.stringify(data.exact_context));
      console.log('🔄 Synced exact_context:', data.exact_context.length, 'items');
    }
    // Public user context → localStorage
    if (data.user_context) {
      localStorage.setItem('soul_user_context', JSON.stringify(data.user_context));
      console.log('🔄 Synced user_context');
    }

    return data;
  } catch (err) {
    console.error('🔄 Pull from server failed:', err);
    return null;
  }
}

// ─── Received Reactions (cross-user visibility) ──────────────────

export async function fetchReceivedReactions() {
  const t = getToken();
  if (!t) return [];
  try {
    const r = await fetch(`${API}/api/user/received-reactions`, { headers: apiHeaders() });
    if (!r.ok) return [];
    const data = await r.json();
    const reactions = (data.reactions || []).map(row => ({
      reactorId: row.reactor_id,
      reactorName: row.reactor_name,
      emoji: (typeof row.data === 'string' ? JSON.parse(row.data) : row.data)?.emoji,
      createdAt: row.created_at,
    }));
    localStorage.setItem('soul_received_vm_reactions', JSON.stringify(reactions));
    return reactions;
  } catch (err) {
    console.error('❌ Failed to fetch received reactions:', err);
    return [];
  }
}

// ─── Profile & Tier ───────────────────────────────────────────────

export async function setUserTier(tier) {
  try {
    localStorage.setItem('soul_premium_tier', tier);
    console.log('✅ Tier saved:', tier);
  } catch (err) {
    console.error('❌ Failed to save tier:', err);
  }
}

// ─── Interview Progress ───────────────────────────────────────────

export async function saveInterviewProgress(stage, step, answers) {
  try {
    const progress = { stage, step, answers };
    localStorage.setItem('soul_interview_progress', JSON.stringify(progress));
    syncToServer('/user/interview-progress', { stage, step, answers, completed: false });
  } catch (error) {
    console.error('❌ Failed to save interview progress:', error);
  }
}

export async function clearInterviewProgress(stage) {
  try {
    const raw = localStorage.getItem('soul_interview_progress');
    if (!raw) return;
    const p = JSON.parse(raw);
    if (p.stage === stage) {
      localStorage.removeItem('soul_interview_progress');
      syncToServer('/user/interview-progress', { stage, step: 0, answers: {}, completed: true });
    }
  } catch (error) {
    console.error('❌ Failed to clear interview progress:', error);
  }
}

// ─── Theme & Preferences ──────────────────────────────────────────

export async function saveTheme(theme) {
  try {
    localStorage.setItem('soul_theme', theme);
    syncToServer('/user/preferences', { theme });
  } catch (error) {
    console.error('❌ Failed to save theme:', error);
  }
}

export async function saveColorScheme(scheme) {
  try {
    localStorage.setItem('soul_color_scheme', scheme);
    syncToServer('/user/preferences', { color_scheme: scheme });
  } catch (error) {
    console.error('❌ Failed to save color scheme:', error);
  }
}

// ─── Dating Preferences ──────────────────────────────────────────

export async function saveDatingPreferences({ age_range_min, age_range_max, max_distance, user_location }) {
  try {
    if (age_range_min != null && age_range_max != null) {
      localStorage.setItem('soul_age_range', JSON.stringify({ min: age_range_min, max: age_range_max }));
    }
    if (max_distance != null) {
      localStorage.setItem('soul_max_distance', String(max_distance));
    }
    if (user_location) {
      localStorage.setItem('soul_user_location', user_location);
    }
    syncToServer('/user/preferences', { age_range_min, age_range_max, max_distance, user_location });
  } catch (error) {
    console.error('❌ Failed to save dating preferences:', error);
  }
}

// ─── App Connections ─────────────────────────────────────────────

export function syncAppConnections(connections) {
  syncToServer('/user/preferences', { app_connections: connections });
}

// ─── Voicemail Reactions ──────────────────────────────────────────

export async function addVoicemailReaction(emoji) {
  try {
    const rawData = localStorage.getItem('soul_voicemail_reactions');
    const current = rawData ? JSON.parse(rawData) : {};
    current[emoji] = (current[emoji] || 0) + 1;
    localStorage.setItem('soul_voicemail_reactions', JSON.stringify(current));
    window.dispatchEvent(new CustomEvent('voicemailReaction', { detail: { emoji, reactions: current } }));
    syncToServer('/user/voicemail-reactions', { reactions: current });
  } catch (error) {
    console.error('❌ Failed to save voicemail reaction:', error);
  }
}

// ─── Match Interactions ───────────────────────────────────────────

export async function saveGivenReaction(matchId, emoji) {
  try {
    const current = JSON.parse(localStorage.getItem('soul_given_reactions') || '{}');
    current[matchId] = emoji;
    localStorage.setItem('soul_given_reactions', JSON.stringify(current));
    syncToServer('/user/match-interaction', { match_id: String(matchId), interaction_type: 'given_reaction', data: { emoji } });
  } catch (error) {
    console.error('❌ Failed to save match reaction:', error);
  }
}

export async function saveReceivedGift(matchId, gift) {
  try {
    localStorage.setItem(`received_gift_${matchId}`, gift);
    syncToServer('/user/match-interaction', { match_id: String(matchId), interaction_type: 'received_gift', data: { gift } });
  } catch (error) {
    console.error('❌ Failed to save received gift:', error);
  }
}

export async function saveReceivedPoll(matchId, poll) {
  try {
    localStorage.setItem(`received_poll_${matchId}`, JSON.stringify(poll));
    syncToServer('/user/match-interaction', { match_id: String(matchId), interaction_type: 'received_poll', data: poll });
  } catch (error) {
    console.error('❌ Failed to save received poll:', error);
  }
}

export async function saveReceivedPhone(matchId, phone) {
  try {
    localStorage.setItem(`received_phone_${matchId}`, phone);
    syncToServer('/user/match-interaction', { match_id: String(matchId), interaction_type: 'received_phone', data: { phone } });
  } catch (error) {
    console.error('❌ Failed to save received phone:', error);
  }
}

export async function saveReceivedMessage(matchId, message) {
  try {
    localStorage.setItem(`received_message_${matchId}`, JSON.stringify(message));
    syncToServer('/user/match-interaction', { match_id: String(matchId), interaction_type: 'received_message', data: message });
  } catch (error) {
    console.error('❌ Failed to save received message:', error);
  }
}

export async function savePollResponse(matchId, option) {
  try {
    localStorage.setItem(`poll_response_${matchId}`, option);
    syncToServer('/user/match-interaction', { match_id: String(matchId), interaction_type: 'poll_response', data: { option } });
  } catch (error) {
    console.error('❌ Failed to save poll response:', error);
  }
}

// ─── Context Sync ────────────────────────────────────────────────

export function syncSecretContext(items) {
  syncToServer('/user/secret-context', { items });
}

export function syncUserContext(contextData) {
  syncToServer('/user/context', { context_data: contextData });
}

export function syncExactContext(items) {
  syncToServer('/user/exact-context', { items });
}

// ─── User Reports ─────────────────────────────────────────────────

export async function saveUserReport(report) {
  try {
    const reports = JSON.parse(localStorage.getItem('soul_user_reports') || '[]');
    reports.push(report);
    localStorage.setItem('soul_user_reports', JSON.stringify(reports));
  } catch (error) {
    console.error('❌ Failed to save user report:', error);
  }
}
